import React from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

const headCellSx = {
  bgcolor: "#1565C0",
  color: "#fff",
  fontWeight: 700,
  whiteSpace: "nowrap",
  py: 1.15,
  fontSize: 14,
};

const actionButtonSx = {
  width: 44,
  height: 38,
  borderRadius: 1.6,
  border: "1px solid #dbeafe",
  bgcolor: "#f8fbff",
  color: "#1565C0",
  "&:hover": {
    bgcolor: "#eef6ff",
  },
};

const deleteButtonSx = {
  width: 44,
  height: 38,
  borderRadius: 1.6,
  border: "1px solid #fecaca",
  bgcolor: "#fff7f7",
  color: "#d32f2f",
  "&:hover": {
    bgcolor: "#ffefef",
  },
};

const ClientTable = ({
  searchTerm,
  setSearchTerm,
  rolFilter,
  setRolFilter,
  filtered,
  loading,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  handleView,
  handleDeleteConfirm,
}) => {
  const renderRolChip = (p) => {
    const roles = [];

    if (p.es_cliente) roles.push("Cliente");
    if (p.es_proveedor) roles.push("Proveedor");
    if (p.es_empleado) roles.push("Empleado");

    if (roles.length === 0) {
      return (
        <Chip
          label="Sin rol"
          size="small"
          variant="outlined"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            "& .MuiChip-label": { px: 1, fontSize: 11.5 },
          }}
        />
      );
    }

    return (
      <Chip
        label={roles.join(" / ")}
        size="small"
        variant="filled"
        sx={{
          borderRadius: 2,
          fontWeight: 600,
          bgcolor: "#ececec",
          "& .MuiChip-label": { px: 1, fontSize: 11.5, color: "#0f172a" },
        }}
      />
    );
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ mb: 2.2 }}
      >
        <TextField
          fullWidth
          placeholder="Buscar persona"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <FormControl sx={{ minWidth: { xs: "100%", md: 250 } }}>
          <InputLabel>Rol</InputLabel>
          <Select
            value={rolFilter}
            label="Rol"
            onChange={(e) => setRolFilter(e.target.value)}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="cliente">Clientes</MenuItem>
            <MenuItem value="proveedor">Proveedores</MenuItem>
            <MenuItem value="empleado">Empleados</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        <TableContainer
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
            maxWidth: "100%",
            "&::-webkit-scrollbar": { height: 10 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#cbd5e1",
              borderRadius: 10,
            },
          }}
        >
          <Table size="small" sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, minWidth: 360 }}>
                  Razón social / Nombre
                </TableCell>
                <TableCell sx={{ ...headCellSx, minWidth: 240 }}>
                  Identificación
                </TableCell>
                <TableCell sx={{ ...headCellSx, minWidth: 180 }}>
                  Rol
                </TableCell>
                <TableCell sx={{ ...headCellSx, minWidth: 180 }}>
                  Teléfono
                </TableCell>
                <TableCell sx={{ ...headCellSx, minWidth: 140 }} align="center">
                  Acción
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Stack spacing={0.75} alignItems="center">
                      <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                        No hay personas para mostrar
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                        Ajusta la búsqueda o cambia el filtro
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                filtered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((p) => (
                    <TableRow
                      key={p.id}
                      hover
                      sx={{
                        "& td": { py: 1.3, borderBottom: "1px solid #edf2f7" },
                        "&:hover": { bgcolor: "#fafcff" },
                      }}
                    >
                      <TableCell sx={{ minWidth: 360 }}>
                        <Typography
                          sx={{
                            fontSize: 13.5,
                            fontWeight: 800,
                            color: "#0f172a",
                            textTransform: "uppercase",
                            lineHeight: 1.2,
                          }}
                        >
                          {p.razon_social || "-"}
                        </Typography>

                        {p.nombre_comercial && (
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: "#64748b",
                              mt: 0.45,
                              textTransform: "uppercase",
                              lineHeight: 1.2,
                            }}
                          >
                            {p.nombre_comercial}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ minWidth: 240 }}>
                        {p.cedula && (
                          <Typography sx={{ fontSize: 12.5, color: "#0f172a" }}>
                            Cédula: {p.cedula}
                          </Typography>
                        )}

                        {p.ruc && (
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              color: "#0f172a",
                              mt: p.cedula ? 0.35 : 0,
                            }}
                          >
                            RUC: {p.ruc}
                          </Typography>
                        )}

                        {!p.cedula && !p.ruc && (
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              color: "#94a3b8",
                              fontWeight: 600,
                            }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ minWidth: 180 }}>
                        {renderRolChip(p)}
                      </TableCell>

                      <TableCell sx={{ minWidth: 180 }}>
                        <Typography sx={{ fontSize: 13, color: "#0f172a" }}>
                          {p.telefono || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell align="center" sx={{ minWidth: 140 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Tooltip title="Dashboard" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleView(p)}
                              sx={actionButtonSx}
                            >
                              <DashboardOutlinedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Eliminar" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteConfirm(p.id)}
                              sx={deleteButtonSx}
                            >
                              <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          sx={{
            borderTop: "1px solid #edf2f7",
            ".MuiTablePagination-toolbar": {
              px: { xs: 1, sm: 2 },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ClientTable;